import { Link, NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Award, Bell, Book, Compass, Home, LogOut, Menu, Moon, ScanLine, Search, Shield, Star, Sun, Tent, User, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import QRScannerModal from "@/components/app/QRScannerModal";

// Post-login navigation for logged-in users. Bottom tab bar on mobile,
// left sidebar on desktop. Tabs shown depend on the user's roles — a pure
// camper only sees Camper tabs, an owner sees Owner tabs, and someone with
// both roles gets a small role-switcher in the header.
export function AppShell({ children }) {
  const { user, logout, currentMode, isCamper, isOwner, isAdmin, api, refresh } = useAuth();
  const { mode: themeMode, auto: themeAuto, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadNotif, setUnreadNotif] = useState(0);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Poll the unread notifications count every 60s to keep the bell badge
  // fresh without hammering the API. Also refresh on route change so a
  // brand-new stamp / level-up notification appears near-instantly.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (!cancelled) setUnreadNotif(data?.unread || 0);
      } catch { /* silent — notif count is best-effort */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [api, location.pathname]);

  // Camper bottom nav — matches the mobile app 1:1:
  //   Home · Explore · [center orange Scan FAB] · Passport · Profile
  // Awards, Challenges, Standings & Inbox stay reachable from the
  // desktop sidebar so power-users on the web can still get to them.
  const camperTabs = [
    { to: "/passport", label: "Home", icon: Home, testId: "nav-home" },
    { to: "/explore", label: "Explore", icon: Search, testId: "nav-explore" },
    { to: "/passport/stamps", label: "Passport", icon: Book, testId: "nav-passport" },
    { to: "/profile", label: "Profile", icon: User, testId: "nav-profile" },
  ];
  const camperCenterFab = {
    to: "/explore",
    label: "Scan",
    icon: ScanLine,
    testId: "nav-scan",
  };
  const ownerTabs = [
    { to: "/owner", label: "Home", icon: Tent, testId: "nav-owner-home" },
    { to: "/explore", label: "Explore", icon: Compass, testId: "nav-owner-explore" },
    { to: "/notifications", label: "Inbox", icon: Bell, testId: "nav-owner-notifications", badge: unreadNotif },
    { to: "/profile", label: "Profile", icon: User, testId: "nav-owner-profile" },
  ];
  const tabs = currentMode === "owner" ? ownerTabs : camperTabs;
  const centerFab = currentMode === "owner" ? null : camperCenterFab;

  // Desktop sidebar — a richer list than the mobile bottom bar so web
  // users can still jump to Achievements, Challenges, Standings and the
  // Inbox in one click.
  const camperSidebarExtras = currentMode === "owner" ? [] : [
    { to: "/achievements", label: "Awards", icon: Award, testId: "nav-achievements" },
    { to: "/notifications", label: "Inbox", icon: Bell, testId: "nav-notifications", badge: unreadNotif },
  ];

  // Admin gets a single "Admin" entry appended to the sidebar so the
  // section is discoverable but doesn't clutter the mobile bottom bar.
  // Profile is already part of the camper AND owner tabs so we don't
  // repeat it here.
  const sidebarTabs = [
    ...tabs,
    ...camperSidebarExtras,
    ...(isAdmin ? [{ to: "/admin", label: "Admin", icon: Shield, testId: "nav-admin" }] : []),
  ];

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const displayName = user?.display_name || user?.name || "Camper";
  const initial = (displayName?.[0] || "M").toUpperCase();
  const roleLabel = currentMode === "owner" ? "Owner" : "Camper";
  const avatarSrc =
    (currentMode === "owner" ? user?.avatar_owner : user?.avatar_camper) ||
    user?.avatar_data || user?.avatar || null;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--surface)" }}>
      {/* Top strip — always visible, holds brand + user badge */}
      <header className="sticky top-0 z-30 border-b" style={{ borderColor: "var(--border-c)", background: "var(--surface)" }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <button
            className="md:hidden -ml-1 p-2 rounded-lg"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="appshell-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="appshell-brand">
            <img src="/assets/brand/logo-header.png" alt="" className="w-9 h-9 object-contain" />
            <span className="hidden sm:block font-extrabold tracking-[0.14em] text-[13px]" style={{ color: "var(--brand)" }}>
              MYADVENTURE-PASSPORT
            </span>
          </Link>
          <div className="flex-1" />
          <div className="hidden md:flex items-center gap-3">
            {/* Day / Night mode toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={`Switch to ${themeMode === "day" ? "night" : "day"} mode`}
              title={`Switch to ${themeMode === "day" ? "night" : "day"} mode`}
              data-testid="appshell-theme-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold transition"
              style={{
                background: "color-mix(in srgb, var(--brand) 8%, transparent)",
                color: "var(--brand)",
                border: "1px solid var(--border-c)",
              }}
            >
              {themeMode === "day"
                ? <Moon className="w-3.5 h-3.5" />
                : <Sun className="w-3.5 h-3.5" />}
              <span>{themeMode === "day" ? "Night" : "Day"}</span>
              {themeAuto && (
                <span
                  data-testid="appshell-theme-auto"
                  className="text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                  style={{ background: "var(--premium)", color: "white" }}
                >
                  Auto
                </span>
              )}
            </button>

            {/* Inline "View as Camper / Owner" toggle. Replaces the old
                CAMPER pill so dual-role users can flip roles from the
                header. Single-role users still see the toggle but the
                other role sits disabled. */}
            <RoleHeaderToggle
              currentMode={currentMode}
              isCamper={isCamper}
              isOwner={isOwner}
              api={api}
              refresh={refresh}
              navigate={navigate}
            />

            {user?.is_premium && (
              <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full inline-flex items-center gap-1"
                    style={{ color: "var(--premium)", background: "color-mix(in srgb, var(--premium) 12%, transparent)" }}
                    data-testid="appshell-premium">
                <Star className="w-3 h-3" fill="currentColor" /> Premium
              </span>
            )}
            <div className="flex items-center gap-2">
              <Link to="/profile" className="flex items-center gap-2 hover:opacity-80" data-testid="appshell-profile-link">
                {avatarSrc ? (
                  <img src={avatarSrc} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full flex items-center justify-center font-extrabold"
                       style={{ background: "var(--brand)", color: "white" }}>
                    {initial}
                  </div>
                )}
                <div className="leading-tight text-right">
                  <div className="text-sm font-bold" style={{ color: "var(--on-surface)" }}>{displayName}</div>
                  {user?.myap_id && (
                    <div className="text-[10px] font-mono" style={{ color: "var(--on-surface-muted)" }}>{user.myap_id}</div>
                  )}
                </div>
              </Link>
            </div>
            <button
              onClick={doLogout}
              className="ml-2 p-2 rounded-lg hover:bg-black/5"
              title="Sign out"
              data-testid="appshell-logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="md:hidden border-t px-4 py-3 flex flex-col gap-2" style={{ borderColor: "var(--border-c)" }}>
            <div className="flex items-center gap-3 py-2">
              {avatarSrc ? <img src={avatarSrc} className="w-10 h-10 rounded-full object-cover" alt="" />
                          : <div className="w-10 h-10 rounded-full flex items-center justify-center font-extrabold"
                                 style={{ background: "var(--brand)", color: "white" }}>{initial}</div>}
              <div>
                <div className="font-bold" style={{ color: "var(--on-surface)" }}>{displayName}</div>
                <div className="text-[11px]" style={{ color: "var(--on-surface-muted)" }}>{roleLabel}{user?.is_premium && " · Premium"}</div>
              </div>
            </div>
            <button onClick={doLogout} className="btn btn-light justify-start" data-testid="appshell-logout-mobile">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto w-full flex-1 flex">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex flex-col gap-2 w-56 shrink-0 px-4 py-6">
          {sidebarTabs.map((t) => (
            <NavLink
              key={t.to} to={t.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition relative ${isActive ? "" : "hover:bg-black/5"}`
              }
              style={({ isActive }) => ({
                background: isActive ? "color-mix(in srgb, var(--brand) 12%, transparent)" : "transparent",
                color: isActive ? "var(--brand)" : "var(--on-surface)",
              })}
              data-testid={t.testId}
            >
              <t.icon className="w-4 h-4" /> {t.label}
              {t.badge > 0 && (
                <span className="ml-auto text-[10px] font-extrabold px-1.5 min-w-[18px] h-[18px] rounded-full inline-flex items-center justify-center"
                      style={{ background: "var(--premium)", color: "white" }}
                      data-testid={`${t.testId}-badge`}>
                  {t.badge > 99 ? "99+" : t.badge}
                </span>
              )}
            </NavLink>
          ))}
        </aside>

        {/* Main */}
        <main className="flex-1 px-4 pt-4 pb-24 md:pb-8 min-w-0" data-testid="appshell-main">
          {/* Global responsive container — narrow, centered stack on
              mobile / PWA; expands to ~6xl on desktop so every page
              inherits the same width contract without repeating
              `max-w-…` classes. Individual pages can still opt out
              by rendering a wider element with negative margins if
              they truly need edge-to-edge (e.g. hero images). */}
          <div className="mx-auto w-full max-w-lg md:max-w-6xl">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile bottom tab bar — mirrors the mobile app: 2 tabs · orange
          center Scan FAB · 2 tabs. Owner mode keeps the classic 3-tab
          layout because there's no camper-style Scan action for owners. */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 border-t z-30 pb-[env(safe-area-inset-bottom)]"
           style={{ background: "var(--surface)", borderColor: "var(--border-c)" }}>
        <div className="flex items-stretch relative">
          {tabs.map((t, i) => {
            const active = location.pathname === t.to;
            const Icon = t.icon;
            // Insert the center Scan FAB between item 2 and item 3 for
            // the camper 4-tab layout.
            const showFabHere = centerFab && i === 2;
            const node = (
              <NavLink key={t.to} to={t.to}
                       className="flex-1 flex flex-col items-center justify-center py-2.5 gap-1 relative"
                       style={{ color: active ? "var(--brand)" : "var(--on-surface-muted)" }}
                       data-testid={`${t.testId}-mobile`}>
                <div className="relative">
                  <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
                  {t.badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 text-[9px] font-extrabold px-1 min-w-[14px] h-[14px] rounded-full inline-flex items-center justify-center"
                          style={{ background: "var(--premium)", color: "white" }}
                          data-testid={`${t.testId}-mobile-badge`}>
                      {t.badge > 9 ? "9+" : t.badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider">{t.label}</span>
              </NavLink>
            );
            return showFabHere
              ? [
                  <div key="fab-slot" className="flex-1" aria-hidden />,
                  node,
                ]
              : node;
          })}
          {centerFab && (
            <button
              type="button"
              onClick={() => setScannerOpen(true)}
              aria-label={centerFab.label}
              data-testid={`${centerFab.testId}-mobile`}
              className="absolute left-1/2 -translate-x-1/2 -top-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition"
              style={{ background: "var(--premium)", color: "white",
                       boxShadow: "0 6px 18px rgba(0,0,0,0.18)" }}
            >
              <centerFab.icon className="w-6 h-6" strokeWidth={2.4} />
            </button>
          )}
        </div>
      </nav>

      <QRScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} />
    </div>
  );
}

// Inline "View as Camper / Owner" toggle rendered in the AppShell header.
// POSTs to /users/mode which flips `current_mode` server-side, then the
// AppShell picks up the new mode from `refresh()` and re-routes to the
// role's landing page.
//
// Single-role users still see the toggle so they discover the feature;
// the role they don't own sits **disabled** (grey, cursor-not-allowed,
// no click handler) — matching the spec on the design annotation.
function RoleHeaderToggle({ currentMode, isCamper, isOwner, api, refresh, navigate }) {
  const flip = async (mode) => {
    if (mode === currentMode) return;
    try {
      await api.post("/users/mode", { mode });
      const u = await refresh();
      const next = (u?.current_mode || mode) === "owner" ? "/owner" : "/passport";
      navigate(next, { replace: true });
    } catch { /* ignore — mode switch is best-effort */ }
  };

  const roles = [
    { id: "camper", label: "Camper", allowed: isCamper },
    { id: "owner", label: "Owner", allowed: isOwner },
  ];

  return (
    <div
      className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full"
      style={{
        background: "color-mix(in srgb, var(--brand) 6%, transparent)",
        border: "1px solid var(--border-c)",
      }}
      data-testid="appshell-role-toggle"
    >
      <span
        className="text-[10px] font-extrabold uppercase tracking-widest"
        style={{ color: "var(--on-surface-muted)" }}
      >
        View as
      </span>
      <div className="flex rounded-full p-0.5" style={{ background: "var(--surface)" }}>
        {roles.map((r) => {
          const active = currentMode === r.id;
          const disabled = !r.allowed;
          return (
            <button
              key={r.id}
              type="button"
              onClick={disabled ? undefined : () => flip(r.id)}
              disabled={disabled}
              aria-pressed={active}
              title={disabled ? `You don't have the ${r.label.toLowerCase()} role yet` : `View as ${r.label}`}
              data-testid={`appshell-role-${r.id}`}
              className={`text-[11px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full transition ${disabled ? "cursor-not-allowed" : ""}`}
              style={{
                background: active ? "var(--brand-deep)" : "transparent",
                color: active ? "white" : (disabled ? "var(--on-surface-muted)" : "var(--brand)"),
                opacity: disabled && !active ? 0.5 : 1,
              }}
            >
              {r.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Route wrapper that requires a signed-in user. Redirects to /login with a
// `next` param pointing back to the attempted route.
export function ProtectedRoute({ children, ownerOnly = false, adminOnly = false }) {
  const { user, loading, isOwner, isAdmin } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="protected-loading">
        <div className="h-8 w-8 rounded-full border-4 border-current border-t-transparent animate-spin" style={{ color: "var(--brand)" }} />
      </div>
    );
  }
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (adminOnly && !isAdmin) {
    // Admin routes silently redirect non-admins to their own dashboard so
    // we don't leak the fact that an admin section exists.
    return <Navigate to={isOwner ? "/owner" : "/passport"} replace />;
  }
  if (ownerOnly && !isOwner) {
    return <Navigate to="/passport" replace />;
  }
  return <AppShell>{children}</AppShell>;
}
